import { Search } from "@outschool/business-rules";
import { SearchFilters } from "@outschool/gql-backend-generated";
import {
  ListingCard_ListingFragment,
  SearchUseCase
} from "@outschool/gql-frontend-generated";
import { searchPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import { useSearchQuery } from "@outschool/ui-components-website";
import { useServerExperimentContext } from "@outschool/ui-experiments";
import _ from "lodash";

import {
  ListRenderableCard,
  RenderableListingsCard,
  RenderableSearchCard
} from "../../../shared/parentDashboard/CardDefinition";

const SAMPLE_POOL_SIZE = 100;
const SAMPLE_SIZE = 20;

function isListingsCardRenderData(
  card: ListRenderableCard
): card is RenderableListingsCard {
  return !!(card as RenderableListingsCard)?.listings;
}

export function useSearchListings(
  globalFilters: SearchFilters,
  localFilters: SearchFilters,
  skip: $TSFixMe,
  cacheOnly: $TSFixMe
) {
  const { currentUser, userTimeZone } = useSession();
  const ages = currentUser
    ? _.compact(currentUser?.children.map(c => c.age)).map(a => a.toString())
    : [];
  const ageFilter = ages.length > 0 ? ages.join(",") : undefined;

  const { loggedOutUserExperimentUid: anonymousId } =
    useServerExperimentContext();

  const { loading, exactListings: listings } = useSearchQuery(
    {
      referrer: document.referrer,
      fetchPolicy: cacheOnly ? "cache-only" : undefined,
      skip: skip || !currentUser,
      variables: {
        searchFilters: {
          activityRatingMin: 4.5,
          age: ageFilter,
          startBefore: Search.RelativeDateFilter.FourteenDays,
          startAfter: Search.RelativeDateFilter.FourteenDays,
          format: "Live online",
          timeZone: userTimeZone,
          ...localFilters,
          ...globalFilters
        },
        limit: SAMPLE_POOL_SIZE,
        skipRating: false,
        withRecentReviews: false,
        matchNames: false,
        anonymousId,
        pageLocation: "PARENT_DASHBOARD",
        useCase: SearchUseCase.Feed
      },
      includeMatchingSections: false,
      source: false
    },
    true
  );

  const showMoreUrl = skip
    ? null
    : searchPath({ q: globalFilters.q, age: ageFilter });

  return {
    loading: !currentUser || loading,
    listings: listings.slice(0, SAMPLE_SIZE) || [],
    showMoreUrl
  };
}

type AgeFilterableListing = {
  activity: {
    age_min: number;
    age_max: number;
  };
};

type DateFilterableListing = {
  activity: {
    paginatedFilteredSections?: {
      data: {
        start_time?: string;
        nextOngoingMeeting?: { start_time: string };
      }[];
    };
  };
};

function ageFilter(listing: AgeFilterableListing, age?: number) {
  if (!age) {
    return true;
  }
  return age >= listing.activity.age_min && age <= listing.activity.age_max;
}

export function dateFilter(
  userTimeZone: $TSFixMe,
  listing: DateFilterableListing,
  startBefore: $TSFixMe,
  startAfter: $TSFixMe
) {
  let mustStartBefore: $TSFixMe, mustStartAfter: $TSFixMe;
  if (startBefore === "SevenDays" || startAfter === "SevenDays") {
    mustStartBefore = dayjs().tz(userTimeZone).add(7, "days").endOf("day");
  } else if (startBefore === "FourteenDays" || startAfter === "FourteenDays") {
    mustStartBefore = dayjs().tz(userTimeZone).add(14, "days").endOf("day");
  } else {
    if (startBefore) {
      mustStartBefore = dayjs.tz(startBefore, userTimeZone).endOf("day");
    }
    if (startAfter) {
      mustStartAfter = dayjs.tz(startAfter, userTimeZone).startOf("day");
    }
  }

  const sections = listing.activity.paginatedFilteredSections?.data;
  return (
    !sections ||
    sections.some(section => {
      const startTime =
        section.nextOngoingMeeting?.start_time || section.start_time;
      return (
        startTime &&
        (!mustStartBefore || dayjs(startTime).isBefore(mustStartBefore)) &&
        (!mustStartAfter || dayjs(startTime).isAfter(mustStartAfter))
      );
    })
  );
}

function userFilter(listing: $TSFixMe, userFilters: string[]) {
  if (userFilters?.length > 0) {
    return userFilters.includes(listing.activity?.leader?.uid);
  }
  return true;
}

export function filterCardListings<
  T extends DateFilterableListing & AgeFilterableListing
>(
  listings: T[],
  filters: SearchFilters,
  userTimeZone: string,
  userFilters: string[] = []
): T[] {
  return listings?.filter(listing => {
    return (
      ageFilter(listing, Number(filters.age)) &&
      dateFilter(
        userTimeZone,
        listing,
        filters.startBefore,
        filters.startAfter
      ) &&
      userFilter(listing, userFilters)
    );
  });
}

export default function useListingsForCard(
  card: ListRenderableCard,
  cacheOnly: boolean,
  localFilters: SearchFilters = {}
): {
  loading: boolean;
  listings: ListingCard_ListingFragment[];
  showMoreUrl: string;
} {
  const { userTimeZone } = useSession();
  const isListingsCard = isListingsCardRenderData(card);
  const cardListings = filterCardListings(
    // @ts-ignore TS(2345): Argument of type 'CardListing[] | null' is not ass... Remove this comment to see the full error message
    (card as RenderableListingsCard).listings,
    localFilters,
    userTimeZone
  );

  const {
    loading: searchLoading,
    listings: searchListings,
    showMoreUrl
  } = useSearchListings(
    (card as RenderableSearchCard).searchFilters,
    localFilters,
    isListingsCard,
    cacheOnly
  );

  return {
    loading: isListingsCard ? false : searchLoading,
    listings: (isListingsCard
      ? cardListings
      : searchListings) as ListingCard_ListingFragment[],
    // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
    showMoreUrl: isListingsCard
      ? (card as RenderableListingsCard).showMoreUrl
      : showMoreUrl
  };
}
